import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircleIcon, ExclamationTriangleIcon, NoSymbolIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { IEbayListingBatchItem } from "../../../models/bulkListings/BulkListing";
import PopOutBox from "../../shared/PopOutBox";
import RecentlyListedTypeCell from "./RecentlyListedTypeCell";
import UnlistedReadyStatus from "../unlistedTable/UnlistedReadyStatus";
import Modal from "../../../layouts/Modal";
import useApiHelper from "../../../hooks/useApiHelper";
import useBatchListingsMutations from "../../../data/useBatchListingsMutations";
import MarketplaceOutcomes from "./MarketplaceOutcomes";


export default function RecentlyListedErrorCell({ batchItem, showExtraInfo = true }: { batchItem: IEbayListingBatchItem, showExtraInfo?: boolean }) {
  const [modalOpen, setModalOpen] = useState(false);
  const { put } = useApiHelper();
  const queryClient = useQueryClient();

  const { cancelMutation } = useBatchListingsMutations();

  const isStuck = !batchItem.isListed && !batchItem.isCancelled && !!batchItem.partData?.listingProcessing;

  if (batchItem.isCancelled) {
    return (
      <div className="flex flex-col gap-y-4 items-center justify-center">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          Cancelled
        </span>
        {showExtraInfo && <RecentlyListedTypeCell type={batchItem.type} />}
      </div>
    );
  }

  if (isStuck) {
    if (batchItem.isProcessing) {
      return (
        <div className="flex flex-col gap-y-2 items-center justify-center">
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
            Processing…
          </span>
          {showExtraInfo && <RecentlyListedTypeCell type={batchItem.type} />}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-y-2 items-center justify-center">
        <button
          onClick={(e) => { e.stopPropagation(); cancelMutation.mutate(batchItem.id); }}
          disabled={cancelMutation.isLoading}
          className="inline-flex items-center gap-x-1 rounded px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <XCircleIcon className="h-4 w-4" />
          {cancelMutation.isLoading ? "Cancelling..." : "Cancel Listing"}
        </button>
        {showExtraInfo && <RecentlyListedTypeCell type={batchItem.type} />}
      </div>
    );
  }

  //eBay rejected the item under its prohibited/restricted items policy. This is permanent, so
  //show the reason plainly instead of a generic error the user might try to "fix".
  if (batchItem.isRestricted) {
    return (
      <>
        <div className="flex flex-col gap-y-2 items-center justify-center">
          <span className="inline-flex items-center gap-x-1 rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">
            <NoSymbolIcon className="h-4 w-4" />
            Restricted
          </span>
          <p className="text-xs text-center text-gray-700 max-w-48">
            {batchItem.restrictedReason ?? "This item is not permitted for sale on eBay."}
          </p>
          {batchItem.restrictedCode && (
            <code className="text-[10px] text-gray-500">{batchItem.restrictedCode}</code>
          )}
          {/* eBay's restriction doesn't stop the other marketplaces - show where it did land. */}
          <MarketplaceOutcomes part={batchItem.partData} compact />
          <button
            onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
            className="text-xs text-primary underline"
          >
            View eBay error
          </button>
          {showExtraInfo && <RecentlyListedTypeCell type={batchItem.type} />}
        </div>
        <Modal open={modalOpen} setOpen={setModalOpen} width="max-w-[70vw]">
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-1">eBay Restriction</h3>
            <p className="text-sm text-gray-600 mb-4">
              eBay's prohibited and restricted items policy blocks this listing. It cannot be listed
              or relisted{batchItem.restrictedCode ? ` (rule ${batchItem.restrictedCode})` : ""}.
            </p>
            <div className="max-h-[70vh] overflow-y-auto space-y-4">
              <pre className="overflow-x-auto break-words text-sm bg-gray-50 p-4 rounded border">{batchItem.errorMessage}</pre>
              <MarketplaceOutcomes part={batchItem.partData} />
            </div>
          </div>
        </Modal>
      </>
    )
  }

  // Every marketplace the part was sent to, with its own answer. Present on rows published since
  // marketplaces started publishing independently; older rows only have the batch message below.
  const hasMarketplaceRows = (batchItem.partData?.marketplaceListings?.length ?? 0) > 0;

  const detailModal = (
    <Modal open={modalOpen} setOpen={setModalOpen} width="max-w-[70vw]">
      <div className="p-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-1">Listing Outcome by Marketplace</h3>
        <p className="text-sm text-gray-600 mb-4">
          {batchItem.partData?.tagNumber && <span className="font-medium">{batchItem.partData.tagNumber} · </span>}
          {batchItem.partData?.offers?.[0]?.title ?? batchItem.partData?.title}
        </p>
        <div className="max-h-[70vh] overflow-y-auto space-y-4">
          <MarketplaceOutcomes part={batchItem.partData} />
          {batchItem.errorMessage && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Batch message</h4>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words text-sm bg-gray-50 p-4 rounded border">{batchItem.errorMessage}</pre>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );

  if (!batchItem.errorMessage) {
    return (
      <>
        <div className="flex flex-col gap-y-2 items-center justify-center">
          <div
            className={hasMarketplaceRows ? "cursor-pointer hover:scale-110" : undefined}
            onClick={(e) => { if (!hasMarketplaceRows) return; e.stopPropagation(); setModalOpen(true); }}
          >
            <CheckCircleIcon className="h-6 w-6 text-green-500" />
          </div>
          <MarketplaceOutcomes part={batchItem.partData} compact />
          <RecentlyListedTypeCell type={batchItem.type} />
        </div>
        {hasMarketplaceRows && detailModal}
      </>
    )
  }

  // Listed with a message means a partial publish: live on at least one marketplace, refused by
  // another. Amber, not red - the part is on sale and the failing marketplace is named below.
  const errorIcon = batchItem.isListed
    ? <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 cursor-pointer" />
    : <ExclamationTriangleIcon className="h-6 w-6 text-red-500 cursor-pointer" />;

  return (
    <>
      <div className="flex flex-col gap-y-2 items-center justify-center">
        <div className="hover:scale-110 z-50" onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}>
          <PopOutBox
            position="left"
            classes="max-w-3xl max-w-[60vw] max-h-[50vh] overflow-y-auto"
            icon={errorIcon}
          >
            <div className="p-2 space-y-2">
              <p className="text-sm font-medium">
                {batchItem.isListed ? "Partially listed" : "Listing failed"} - click to see every marketplace's response
              </p>
              {hasMarketplaceRows ? (
                <MarketplaceOutcomes part={batchItem.partData} />
              ) : (
                <pre className="whitespace-pre-wrap break-words">{batchItem.errorMessage}</pre>
              )}
            </div>
          </PopOutBox>
        </div>
        <MarketplaceOutcomes part={batchItem.partData} compact />
        {showExtraInfo && (
          <>
            <RecentlyListedTypeCell type={batchItem.type} />
            {!batchItem.isListed && <UnlistedReadyStatus part={batchItem.partData} />}
          </>
        )}

      </div>
      {detailModal}
    </>
  )
}